import React, { useEffect, useRef, useState } from 'react';
// import { RotateCcw } from 'lucide-react';
import MeasurementEmbeddedApp, { faceAttributeValue, ErrorCodes } from '@nuralogix.ai/web-measurement-embedded-app';
import { parseResults } from '@/shared/utils/nuralogixHelpers';
import {
    addFollowUp, addFollowUpParams, datagetEtiquetas, getEtiquetas, getEtiquetasParams, getStudyId, getToken, updateAppointment,
    updateAppointmentParams, updateLocalizador, updateLocalizadorParams, createTreatmentParams, crearLote, EnviarPlanDeTratamientosParams,
    enviarPlanDeTratamientos
} from "@/core/api/sigma"
import { useAuthStore } from '@/core/auth/auth.store';
import { useNavigate } from 'react-router-dom';
import { env } from '@/config/env';
import { diagnosticosyTratamientosByPaisQA, diagnosticosyTratamientosByPaisPRD } from '@/shared/utils/diagnosticosyTratamientosByPais';
import { clinicaYDoctorByPaisQA, clinicaYDoctorByPaisPRD } from '@/shared/utils/clinicaYDoctorByPais';
import { InfoModal } from '@/features/infoModal/pages/InfoModal';
import { Footer } from '@/shared/components/layout/Footer';
import Swal from 'sweetalert2';

/**
 * Elimina el container viejo del wrapper y crea uno nuevo limpio (sin Shadow Root).
 * El div de NuraLogix NO vive en el JSX de React para que React no pise el ref.
 */
const createFreshContainer = (
    wrapperRef: { current: HTMLDivElement | null },
    containerRef: { current: HTMLDivElement | null }
): void => {

    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    // 1. Remover el nodo trackeado desde donde quiera que NuraLogix lo haya movido
    if (containerRef.current) {
        containerRef.current.parentNode?.removeChild(containerRef.current);
        containerRef.current = null;
    }
    // 2. Vaciar cualquier residuo que NuraLogix haya dejado dentro del wrapper
    while (wrapper.firstChild) {
        wrapper.removeChild(wrapper.firstChild);
    }
    // Crear nodo nuevo, virgen, sin ningún Shadow Root
    const next = document.createElement('div');
    next.id = 'measurement-container';
    next.className = 'scan-placeholder';
    next.style.cssText = 'width:100%;border-radius:8px;position:relative;-webkit-transform:translateZ(0);transform:translateZ(0);';
    wrapper.appendChild(next);
    containerRef.current = next;
};

const VitalsScanning = () => {
    //Referencias para el DOM y para la instancia de la app
    // wrapperRef: div del JSX que React controla
    // containerRef: div imperativo que NuraLogix controla (NO está en el JSX)
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const appRef = useRef(null);
    const description = (
        <>
            <div style={{ textAlign: 'justify' }}>
                <p>Si no podemos calcular tu medición, revisa lo siguiente antes de intentarlo nuevamente:</p>
                <ul style={{ listStyleType: 'none', padding: 0 }}>
                    <li className="mb-2"><strong>• Verifica tu conexión a internet:</strong> asegúrate de tener una conexión estable durante todo el proceso.</li>
                    <li className="mb-2"><strong>• Evita moverte:</strong> mantente lo más quieto posible y procura que tu dispositivo también esté fijo.</li>
                    <li className="mb-2"><strong>• Mejora la iluminación:</strong> ubícate en un lugar bien iluminado, con luz uniforme en tu rostro y sin sombras.</li>
                    <li className="mb-2"><strong>• Acércate a la cámara:</strong> coloca tu rostro dentro del encuadre y mantente a una distancia cercana.</li>
                    <li className="mb-2"><strong>• Despeja tu rostro:</strong> evita objetos que lo cubran, como gafas oscuras, gorras o cabello sobre la cara.</li>
                    <li className="mb-2"><strong>• Limpia la lente de la cámara:</strong> asegúrate de que la cámara esté limpia y sin manchas.</li>
                    <li className="mb-2"><strong>• Prueba con otro dispositivo:</strong> si el problema continúa, intenta usar otro celular, tablet o computadora.</li>
                </ul>
                
                <p><strong>Navegadores recomendados:</strong> </p>
                <ul style={{ listStyleType: 'none', padding: 0 }}>
                    <li className="mb-2">Google Chrome</li>
                    <li className="mb-2">Microsoft Edge</li>
                    <li className="mb-2">Safari</li>
                </ul>
                <p>Si continúas teniendo inconvenientes, intenta desde otro dispositivo.</p>
            </div>
        </>
    );

    const [modalData, setModalData] = React.useState({
        isOpen: false,
        title: '',
        icon: '',
        content: ''
    });
    const closeModal = () => setModalData({ ...modalData, isOpen: false });

    const navigate = useNavigate();

    // Estados para manejar la UI
    const [isScanning, setIsScanning] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuthStore()

    // Refs para mantener valores actualizados dentro de callbacks sin recrear la función
    const navigateRef = useRef(navigate);
    const userRef = useRef(user);
    const isScanningRef = useRef(false);
    useEffect(() => { navigateRef.current = navigate; }, [navigate]);
    useEffect(() => { userRef.current = user; }, [user]);
    useEffect(() => { isScanningRef.current = isScanning; }, [isScanning]);

   /* async function handleRestart() {
        setError(null);
        initMeasurementApp();
    }*/

    async function initMeasurementApp() {
        const navigate = navigateRef.current;
        // Para la inicialización del SDK leemos user una sola vez. Los callbacks
        // (on.results, on.error) lo releen desde userRef al dispararse para no
        // quedar atrapados con una referencia obsoleta.
        const user = userRef.current;

        try {
            setError(null);

            // Destruir instancia previa si existe y crear contenedor limpio
            await clearScanning();
            createFreshContainer(wrapperRef, containerRef);

            // Obtener credenciales de tu backend
            const studyIdRes = await getStudyId()
            const tokenRes = await getToken()

            if (!studyIdRes.studyid || !tokenRes.token) {
                throw new Error('Error obteniendo credenciales');
            }

            // Crear instancia
            appRef.current = new MeasurementEmbeddedApp();


            // Configurar eventos
            appRef.current.on.results = async (measurementResults) => {
                // Leer user desde el ref para que el callback vea siempre el estado actual
                // del store (no la referencia capturada al inicio del init, que podía estar vacía).
                const user = userRef.current;

                //Si viene errors en los resultados, no continuar con el flujo normal, mostrar mensaje de error y permitir reintentar
                if (measurementResults?.Error?.Code !== 'OK') {
                    console.error('Error en los resultados de la medición:', measurementResults);

                    await appRef.current?.cancel(true);

                    const ccita = user.ccitaCreated || user.ccita;
                    const nlocaliza = user.nlocalizaCreated || user.nlocaliza;

                    if (!env.isDevelopment) await handleUpdateAppointment(ccita, user.ccedasegurado, 'Z');

                    const xmensaje = (f, h) => `Escaneo fallido de Signos Vitales el ${f} a las ${h}. error: ${measurementResults?.Error?.Code || 'Error desconocido'}`
                    if (!env.isDevelopment) await handleAddFollowUp(user.cpais, nlocaliza, xmensaje);

                    const userMessage = 'El escaneo de Signos Vitales no pudo ser procesado. Por favor, intente nuevamente. Recuerde mantener su rostro dentro del encuadre, con buena iluminación y sin moverse durante el proceso.';
                    setError(userMessage);
                    await Swal.fire({
                        html: 'Se presentó una novedad en el proceso. La página se reiniciará automáticamente para continuar.',
                        icon: 'warning',
                        confirmButtonText: 'Aceptar',
                        timer: 4000,
                        //timerProgressBar: true,
                    });
                    window.location.reload();
                    return;
                }


                await clearScanning();

                console.log('Resultados de la medición:', measurementResults);

                setIsScanning(false);
                setIsProcessing(true);

                user.results = parseResults(measurementResults);
                const ccita = user.ccitaCreated || user.ccita;
                const nlocaliza = user.nlocalizaCreated || user.nlocaliza;

                await handleUpdateAppointment(ccita, user.ccedasegurado, 'W')
                await handleUpdateLocalizador(user.cpais, nlocaliza)

                let cdiagnos = '', ctrata = '', cclinica = '', cdoctor = '';
                if (env.isProduction) {
                    console.log("Usando datos de diagnóstico y tratamiento de PRD para el país", user.cpais);
                    ({ cdiagnos, ctrata } = diagnosticosyTratamientosByPaisPRD[user.cpais]);
                    ({ cclinica, cdoctor } = clinicaYDoctorByPaisPRD[user.cpais]);
                } else {
                    console.log("Usando datos de diagnóstico y tratamiento de QA para el país", user.cpais);
                    ({ cdiagnos, ctrata } = diagnosticosyTratamientosByPaisQA[user.cpais]);
                    ({ cclinica, cdoctor } = clinicaYDoctorByPaisQA[user.cpais]);
                }
                const params: createTreatmentParams = {
                    cpais: user.cpais,
                    cusuario: '9999999999',
                    ccategoria: '9999',
                    itipo: 'L',
                    nlocaliza: nlocaliza,
                    cdiagnos,
                    ctrata,
                    ndiente: '0',
                    ncantrep: '1',
                    ibaremo: '1',
                    iorigen: 'W',
                    pDataBaremo: {
                        cciaseg: user.detailLocalizador?.cciaseg || user.detailLocalizadorCreated?.cciaseg,
                        ccliente: user.detailLocalizador?.ccliente || user.detailLocalizadorCreated?.ccliente,
                        cplan: user.detailLocalizador?.cplan || user.detailLocalizadorCreated?.cplan,
                        cfecha: user.detailLocalizador?.fcontacto || user.detailLocalizadorCreated?.fcontacto,
                        fcontacto: user.detailLocalizador?.fcontacto || user.detailLocalizadorCreated?.fcontacto,
                        tlocaliza: user.detailLocalizador?.tlocaliza || user.detailLocalizadorCreated?.tlocaliza,
                        cpais: user.cpais,
                        cdoctor: user.detailLocalizador?.cdoctor || user.detailLocalizadorCreated?.cdoctor || cdoctor,
                        cclinica: user.detailLocalizador?.cclinica || user.detailLocalizadorCreated?.cclinica || cclinica,
                        itclave: user.detailLocalizador?.itclave || user.detailLocalizadorCreated?.itclave || '',
                    }
                }
                await handleCrearLote(params)

                await handleEnviarPlanDeTratamientos(user.cpais, nlocaliza)

                const xmensaje = (f, h) => `Escaneo exitoso de Signos Vitales el ${f} a las ${h}`
                await handleAddFollowUp(user.cpais, nlocaliza, xmensaje);

                navigate("/vitalsresults", { replace: true });
            };

            appRef.current.on.error = async (err) => {
                // Leer user desde el ref para que el callback vea siempre el estado actual
                // del store, no la referencia capturada al inicio del init.
                const user = userRef.current;

                console.error('Error de medición:', err.code, err.message);
                if (err.code === ErrorCodes.WEBSOCKET_DISCONNECTED && !isScanningRef.current) {
                    console.warn("WebSocket se desconectó antes o después de iniciar el escaneo");
                    return;
                }

                // El SDK necesita cancel(true) para resetear su estado interno antes de poder reutilizarse
                const resetCodes = [
                    ErrorCodes.COLLECTOR,
                    ErrorCodes.WEBSOCKET_DISCONNECTED,
                    ErrorCodes.FACE_NONE,
                    ErrorCodes.MEASUREMENT_LOW_SNR,
                    ErrorCodes.WORKER_ERROR,
                    ErrorCodes.PAGE_NOT_VISIBLE,
                ];
                if (resetCodes.includes(err.code)) {
                    try {
                        await appRef.current?.cancel(true);
                    } catch (e) {
                        await appRef.current?.cancel(true);
                        console.warn('Failed to cancel after error code', err.code, e);
                    }
                }

                const ccita = user.ccitaCreated || user.ccita;
                const nlocaliza = user.nlocalizaCreated || user.nlocaliza;

                // Mensaje amigable según el código de error
                let userMessage = 'Ocurrió un error durante el escaneo.';
                if (err.code === ErrorCodes.MEASUREMENT_PREPARE_FAILED) {
                    userMessage = 'Las credenciales son inválidas o están vencidas. Contacte al administrador.';
                } else if (err.code === ErrorCodes.ASSET_DOWNLOAD_FAILED) {
                    userMessage = 'No se pudieron descargar los recursos necesarios. Verifique su conexión.';
                } else if (err.message.includes('Chunk contained insufficient frames')) {
                    userMessage = 'Por favor asegúrese de que su rostro esté completamente visible y bien iluminado durante el escaneo.';
                } else if (err.code === ErrorCodes.COLLECTOR) {
                    userMessage = 'Asegúrese de que su cara esté dentro del encuadre, con buena iluminación y sin moverse durante el proceso.';

                } else if (err.message) {
                    userMessage = err.message;
                }

                setIsScanning(false);
                setError(userMessage);

                if (!env.isDevelopment) handleUpdateAppointment(ccita, user.ccedasegurado, 'Z');

                const xmensaje = (f, h) => `Escaneo fallido de Signos Vitales el ${f} a las ${h}. error: ${err.message || 'Error desconocido'}`
                if (!env.isDevelopment) handleAddFollowUp(user.cpais, nlocaliza, xmensaje);

            };

            // Si el profile no está en el store (ej. recarga de página), intentar restaurarlo de sessionStorage
            if (!user.profile?.age) {
                const savedProfile = sessionStorage.getItem('vitalsProfile');
                if (savedProfile) {
                    try {
                        user.profile = JSON.parse(savedProfile);
                        console.log('Perfil restaurado desde sessionStorage:', user.profile);
                    } catch (e) {
                        console.warn('No se pudo parsear el perfil de sessionStorage', e);
                    }
                }
            }

            if (env.isDevelopment && !user.profile.age) {
                user.profile = {
                    age: 30,
                    heightCm: 170,
                    weightKg: 70,
                    sex: faceAttributeValue.SEX_ASSIGNED_MALE_AT_BIRTH,
                    smoking: faceAttributeValue.SMOKER_TRUE,
                    bloodPressureMedication: faceAttributeValue.BLOOD_PRESSURE_MEDICATION_FALSE,
                    diabetes: faceAttributeValue.DIABETES_NONE,
                }

                console.warn("No se encontró perfil de usuario, usando datos de prueba en desarrollo");
                console.warn(user.profile);
            } else if (!user.profile.age) {
                console.warn('No se encontró perfil de usuario para la medición, redirigiendo a /sessionfinished');
                
                navigate('/sessionfinished', { replace: true });
                return;
            } else {
                console.log('Perfil de usuario encontrado para la medición:', user.profile);
            }


            // Inicializar widget en el contenedor referenciado
            await appRef.current.init({
                container: containerRef.current, // <-- div imperativo creado por createFreshContainer
                //appPath: 'https://unpkg.com/@nuralogix.ai/web-measurement-embedded-app/dist',
                appPath: './wmea',
                language: 'es',
                settings: {
                    token: tokenRes.token,
                    refreshToken: tokenRes.refreshtoken,
                    studyId: studyIdRes.studyid,
                    measurementOptions: {
                        partnerId: user.nlocalizaCreated ? String(user.nlocalizaCreated) : String(user.nlocaliza), // Puedes usar cualquier identificador único aquí
                    },
                },
                config: {
                    checkConstraints: true,
                    cameraFacingMode: 'user',
                    cameraAutoStart: window.innerWidth > 1024,//Solo en PC es true, tablet y mobile con botón
                    measurementAutoStart: true,
                    cancelWhenLowSNR: true,
                },
                profile: {
                    ...user.profile,
                    height: user.profile.heightCm,
                    weight: user.profile.weightKg,
                },
            });

            setIsScanning(true);
        } catch (err) {
            console.error('Error de inicialización:', err);
            await clearScanning();
            setError('No se pudo inicializar la aplicación: ' + err.message);
        }
    }

    // Inicialización al montar el componente (solo una vez)
    useEffect(() => {
        initMeasurementApp();

        // Función de limpieza al desmontar el componente (evita que la cámara quede prendida)
        return () => {
            clearScanning();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function clearScanning() {
        const wrapper = wrapperRef.current;
        if (appRef.current) {
            await appRef.current.destroy();
            appRef.current = null;
        }

        if (wrapper?.contains(containerRef.current)) {
            wrapper.removeChild(containerRef.current);
        }

        if (containerRef.current) {
            containerRef.current = null;
        }

        const element = document.getElementById('measurement-container');
        if (element) {
            element.remove();
        }
 
    }

    return (
        <div className="vitals-wrapper">
            <div className="container-vitals-top">
                <section className="section-vitals-top">
                    {/* Barra de Progreso - Paso 3 */}
                    
                    <div className="problema-scan"
                        onClick={() => setModalData({
                            isOpen: true,
                            title: 'Problemas que podrían afectar la medición',
                            icon: null,
                            content: description
                        })} >
                        <i className="icon icon_enlinea_ayuda_hover"></i>
                        <span className='text-mod'>¿Problemas con la medición?</span> <br />
                    </div>


                    <div className="form-vitals">

                        {isProcessing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' }}>
                                <div className="spinner-vitals" />
                                <p>Procesando resultados...</p>
                            </div>
                        ) : (
                            <>
                                {error && (
                                    <div style={{ marginTop: '10px', textAlign: 'center' }}>
                                        <p style={{ color: 'red' }}>{error}</p>
                                        {/* <button
                                            onClick={handleRestart}
                                            style={{ marginTop: '8px', padding: '10px 20px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                        >
                                            <RotateCcw size={18} />
                                            Volver a intentar
                                        </button> */}
                                    </div>
                                )}

                                {/* Wrapper de React. El div interno de NuraLogix se crea imperativament con JS */}
                                <div ref={wrapperRef} style={{ width: '100%', minHeight: '300px' }} />

                            </>
                        )}
                    </div>

                </section>
            </div>

            <Footer/>

            <InfoModal
                isOpen={modalData.isOpen}
                onClose={closeModal}
                title={modalData.title}
                icon={modalData.icon}
            >
                <p>{modalData.content}</p>
            </InfoModal>
        </div>
    );
};

const handleUpdateAppointment = async (ccita: string, ccedasegurado: string, iestado: string) => {
    try {
        const params: updateAppointmentParams = {
            ccita, ccedasegurado, iestado
        };
        await updateAppointment([params]);
    } catch (error) {
        console.error("Error actualizando estado de cita:", error);
    }
};

const handleUpdateLocalizador = async (cpais: string, nlocaliza: string) => {
    try {
        const params: updateLocalizadorParams = {
            cpais, nlocaliza, iestado: 'C'
        };
        await updateLocalizador([params]);
    } catch (error) {
        console.error("Error actualizando estado de localizador:", error);
    }
};

const handleAddFollowUp = async (cpais: string, nlocaliza: string, mensajeBuilder: (fecha: string, hora: string) => string) => {
    try {
        const pInputEtiquetas: getEtiquetasParams = {
            cpais, xdescripcion: 'información'
        }
        //Obtener la etiqueta necesaria
        const etiquetas: datagetEtiquetas[] = await getEtiquetas(pInputEtiquetas);

        if (!etiquetas.length) return;

        // Preparar el mensaje formateado
        const ahora = new Date();
        const fecha = ahora.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const hora = ahora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false });
        const mensaje = mensajeBuilder(fecha, hora);

        // Enviar el seguimiento
        const params: addFollowUpParams = {
            cpais, nlocaliza, xdetalle: mensaje,
            cetiqueta: etiquetas[0].cetiqueta,
            cusuario: '9999999999'
        };

        await addFollowUp([params]);
    } catch (error) {
        console.error("Error al añadir seguimiento:", error);
    }
};

const handleCrearLote = async (params: createTreatmentParams) => {
    try {
        await crearLote(params);
    } catch (error) {
        console.error("Error creando lote:", error);
    }
}

const handleEnviarPlanDeTratamientos = async (cpais: string, nlocaliza: string) => {
    const params: EnviarPlanDeTratamientosParams = {
        cpais,
        cusuario: '9999999999',
        ccategoria: '9999',
        nlocaliza
    }
    try {
        await enviarPlanDeTratamientos(params);
    } catch (error) {
        console.error("Error enviando plan de tratamientos:", error);
    }
}



export { VitalsScanning };